import { referralRequestDetail } from "../constants/actionTypes";

const defaultModals = {
  matchesInfo: false,
  missingInfo: false,
  message: false,
  successMessage: false,
  questionOpenToOther: false,
  contactInfo: false
};

const defaultState = {
  case: {},
  error: null,
  followups: [],
  matches: [],
  modals: {
    ...defaultModals
  },
  categoriesProfessions: {},
  preloader: false,
  history: [],
  messageModalName: "",
  messageModalListingId: null,
  lastSentListingMessageId: null,
  // GROWTH-7376: the lead the last sent message belongs to, and (as `leadId` inside `sendMessageError`) the lead a failed
  // send was for, so a late answer is shown only on that lead's page.
  lastSentLeadId: null,
  sendMessageError: null,
  isFailedEmailVerification: false,
  newsletters: {},
  contactInfoData: {},
  contactInfoError: null,
  // GROWTH-6568: outcome of the last follow-up answers save, keyed by lead so a late response
  // from a lead the member already left never shows on another one.
  followupsSavedLeadId: null,
  followupsSaveError: null,
  // GROWTH-6571: why the last delete failed, keyed by lead (`{ leadId, status }`), and why the last
  // "Edit description" save failed. `error` below is written by several failures and read by no component.
  removeError: null,
  caseSaveError: null,
  // GROWTH-6580: why loading the request failed, keyed by lead (`{ leadId, status, isNetworkError, requestId }`) so the
  // page shows it only on that lead's URL. Only the case request writes it: `error` is also written by the practices
  // and case-save failures, which must not replace a loaded page.
  loadError: null,
  practices: []
};

// A failure names its lead by the id getCase was given: the URL's id (a string) or, after a save, the saved lead_id; the
// API's lead_id may be a number or a string, so both sides are compared as strings.
const isStoredLead = (storedCase, leadId) => leadId != null && String(storedCase.lead_id) === String(leadId);

export default function(state = defaultState, action) {
  const { type, payload } = action;
  switch (type) {
    case referralRequestDetail.GET_CASE_SUCCESS:
      return {
        ...state,
        case: payload.lead,
        followups: payload.followups.reduce((total, current) => {
          return total.find(item => item.practice_followup_id === current.practice_followup_id)
            ? total
            : [
                ...total,
                {
                  ...current,
                  answer: payload.followups
                    .filter(elem => elem.practice_followup_id === current.practice_followup_id)
                    .map(item => item.answer)
                }
              ];
        }, []),
        matches: payload.matches,
        history: payload.history,
        newsletters: payload.newsletters,
        preloader: false,
        isFailedEmailVerification: payload.isFailedEmailVerification,
        followupsSavedLeadId: null,
        followupsSaveError: null,
        removeError: null,
        caseSaveError: null,
        loadError: null
      };

    case referralRequestDetail.GET_CASE_PRACTICES:
      return {
        ...state,
        categoriesProfessions: payload
      };

    case referralRequestDetail.GET_CASE_FAIL:
      return {
        ...state,
        // GROWTH-7287: the server refused the request the store holds, so it is not kept to be drawn again once another
        // request's failure has replaced `loadError`. A failure for any other request (a late answer for one the member
        // already left) leaves the stored one alone.
        case: isStoredLead(state.case, payload?.leadId) ? {} : state.case,
        preloader: false,
        error: payload,
        loadError: payload
      };

    case referralRequestDetail.SUBMIT_CASE_DATA_SUCCESS:
      return {
        ...state,
        case: {
          added: state.case.added,
          status: state.case.status,
          ...payload
        },
        preloader: false,
        caseSaveError: null,
        modals: {
          ...defaultModals
        }
      };

    case referralRequestDetail.SUBMIT_CASE_DATA_FAIL:
      // GROWTH-6571: keep the failure where the CaseInfo modal can show it; the modal stays open.
      return {
        ...state,
        error: payload,
        caseSaveError: payload,
        preloader: false
      };

    case referralRequestDetail.GET_PRACTICES_FAIL:
      return {
        ...state,
        error: payload,
        preloader: false
      };

    case referralRequestDetail.REMOVE_CASE_FAIL:
      return {
        ...state,
        removeError: { leadId: payload?.leadId ?? null, status: payload?.status ?? null },
        preloader: false
      };

    case referralRequestDetail.SET_CASE_PRELOADER:
      return {
        ...state,
        preloader: payload
      };

    case referralRequestDetail.UPDATED_FOLLOWUPS:
      return {
        ...state,
        preloader: false,
        modals: {
          ...defaultModals
        },
        followupsSavedLeadId: payload?.leadId ?? null,
        followupsSaveError: null
      };

    case referralRequestDetail.UPDATED_FOLLOWUPS_FAIL:
      return {
        ...state,
        preloader: false,
        followupsSavedLeadId: null,
        followupsSaveError: {
          leadId: payload?.leadId ?? null,
          message: payload?.message || "Unable to save your answers. Please try again.",
          status: payload?.status || null
        }
      };

    case referralRequestDetail.GET_PRACTICES:
      return {
        ...state,
        practices: payload
      };

    case referralRequestDetail.CLEAR_ALL_MODALS:
      return {
        ...state,
        modals: {
          ...defaultModals
        },
        messageModalName: "",
        messageModalListingId: null,
        caseSaveError: null
      };

    case referralRequestDetail.TOGGLE_ACTIVITY_OF_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          [payload.modal]: payload.isActive
        },
        caseSaveError: null
      };

    case referralRequestDetail.SEND_MATCH_MESSAGE:
      return {
        ...state,
        modals: {
          ...state.modals,
          message: false,
          successMessage: true
        },
        lastSentListingMessageId: payload?.listingMessageId || null,
        lastSentLeadId: payload?.leadId ?? null,
        sendMessageError: null,
        preloader: false
      };

    case referralRequestDetail.SEND_MATCH_MESSAGE_FAIL:
      return {
        ...state,
        sendMessageError: {
          message: payload?.message || "Unable to send your message. Please try again.",
          status: payload?.status || null,
          leadId: payload?.leadId ?? null
        },
        preloader: false
      };

    case referralRequestDetail.TOGGLE_ACTIVITY_MESSAGE_MODAL:
      return {
        ...state,
        modals: {
          ...state.modals,
          message: payload.isActive
        },
        messageModalName: payload.name,
        messageModalListingId: payload.isActive ? payload.listingId : null,
        sendMessageError: payload.isActive ? null : state.sendMessageError
      };

    case referralRequestDetail.SET_CONTACT_INFO_DATA:
      return {
        ...state,
        contactInfoData: payload,
        contactInfoError: null
      };

    case referralRequestDetail.CHANGE_CONTACT_INFO:
      return {
        ...state,
        contactInfoData: {},
        contactInfoError: null,
        modals: {
          ...defaultModals
        },
        preloader: false,
        case: { ...state.case, ...payload }
      };

    case referralRequestDetail.CHANGE_CONTACT_INFO_FAIL:
      // GROWTH-7035: keep the failure so the modal can tell the member the save was rejected.
      return {
        ...state,
        contactInfoError: payload,
        preloader: false
      };

    default:
      return state;
  }
}
